import {
  browserTracingIntegration,
  type ErrorEvent,
  featureFlagsIntegration,
  graphqlClientIntegration,
  init,
  replayIntegration,
  SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN,
} from '@sentry/browser';
import config from './config';
import { isPageReloading } from './reloadOnStaleChunk';

const SENTRY_ORG_URL = 'https://hiboo.sentry.io';

/** The spans quota is shared with the API, and a sampled trace also carries the API spans it triggers. */
const PRODUCTION_PAGELOAD_TRACES_SAMPLE_RATE = 0.1;
const PRODUCTION_TRACES_SAMPLE_RATE = 0.04;
const PRE_PRODUCTION_TRACES_SAMPLE_RATE = 0.25;

/** `sentry.op` is only set after sampling, so pageloads are told apart by their origin. */
const PAGELOAD_SPAN_ORIGIN = 'auto.pageload.browser';

const FINGERPRINT_BY_SENTRY_LABEL = new Map([
  ['Failed to fetch', 'failed-to-fetch'],
]);

const getCustomFingerprint = (event: ErrorEvent): string[] | undefined => {
  for (const [label, fingerprint] of FINGERPRINT_BY_SENTRY_LABEL) {
    const matchesLabel = event.exception?.values?.some((exception) =>
      exception.value?.startsWith(label),
    );
    if (matchesLabel) {
      return [fingerprint];
    }
  }
  return undefined;
};

if (config.sentryTracking && config.SENTRY_DSN) {
  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || config.env || 'development';
  const isProduction = environment === 'production';
  const isDeployed = environment !== 'development';
  const replay = replayIntegration({
    maskAllText: false,
    maskAllInputs: false,
  });
  try {
    init({
      dsn: config.SENTRY_DSN,
      release: import.meta.env.VITE_APP_VERSION || undefined,
      environment,
      integrations: [
        browserTracingIntegration({
          shouldCreateSpanForRequest: (url) => {
            return url !== `${config.apiURL}/events`;
          },
        }),
        featureFlagsIntegration(),
        graphqlClientIntegration({
          endpoints: [`${config.apiURL}/graphql`],
        }),
        replay,
      ],
      beforeSend: (event) => {
        if (isPageReloading()) {
          return null;
        }
        const fingerprint = getCustomFingerprint(event);
        if (fingerprint) {
          event.fingerprint = fingerprint;
        }
        const replayId = replay.getReplayId();
        if (replayId) {
          event.tags = {
            ...event.tags,
            replayUrl: `${SENTRY_ORG_URL}/replays/${replayId}/`,
          };
        }
        return event;
      },
      tracesSampler: ({ attributes }) => {
        if (!isDeployed) {
          return 0;
        }
        if (!isProduction) {
          return PRE_PRODUCTION_TRACES_SAMPLE_RATE;
        }
        return attributes?.[SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN] ===
          PAGELOAD_SPAN_ORIGIN
          ? PRODUCTION_PAGELOAD_TRACES_SAMPLE_RATE
          : PRODUCTION_TRACES_SAMPLE_RATE;
      },
      tracePropagationTargets: [
        /^https?:\/\/localhost/,
        /^https?:\/\/127\.0\.0\.1/,
        /^https?:\/\/0\.0\.0\.0/,
        /^https?:\/\/([a-zA-Z0-9-]+\.)*hiboo\.io/,
        config.apiURL,
      ],
      replaysOnErrorSampleRate: isDeployed ? 1.0 : 0,
      replaysSessionSampleRate: 0,
    });
  } catch (err) {
    console.error('Error initializing Sentry:', err);
  }
}
